import InputComponent from "components/FormElements/inputs";
import { Formik, FormikProps } from "formik";
import { LoginInterface } from "interfaces/auth";
import { useLogin } from "lib/query/user";
import React from "react";
import { loginSchema } from "utils/validationSchema";
import toast from "react-hot-toast";
import { AuthButtonContainer, ForgotPasswordText } from "./styles";
import Bugsnag from "@bugsnag/js";
import config from "config";
import CustomButton from "components/CustomButton";
import storage from "utils/storage";
import {
  defaultBusinessKey,
  idleStatusKey,
  accessControlFailedStatusKey,
  memberKey,
  userKey,
  pwdKey
} from "utils/constants";
import { encrypt_key, encrypt_userobj } from "utils/auth-util";
import { useHistory } from "react-router";
import { Routes } from "routes/routes-constants";
import PasswordInputComponent from "components/FormElements/inputs/passwordInput";
import { ITeamMemberResponseObject, MerchantInterface } from "interfaces/users";
import UserContext from "context/userContext";
import { DismissibleAlert } from "layout/AppLayout/styles";
import { rudderanalytics } from "lib/eventGenerate";
import { useMediaQuery } from "lib/hooks";
import { device } from "styles";
import { identifyOnHotjar, trackEventForHotjar } from "utils/hotjar";
import moment from "moment";

const initialValues: LoginInterface = {
  email: "",
  password: ""
};

const Login = () => {
  const { mutate: userLogin, isLoading } = useLogin();
  const { storeAuthHeader } = React.useContext(UserContext);
  const history = useHistory();
  const isMobile = useMediaQuery(device.mobile_);

  React.useEffect(() => {
    const pageReferer = document.referrer || "";
    const idleMark = localStorage.getItem(idleStatusKey);
    const accessControlFailedMark = localStorage.getItem(
      accessControlFailedStatusKey
    );
    const isIdle = idleMark === "true";
    const accessControlFailed = accessControlFailedMark === "true";

    if (isIdle) {
      localStorage.setItem(idleStatusKey, "false");
    }

    if (accessControlFailed) {
      localStorage.setItem(accessControlFailedStatusKey, "false");
    }

    if (isIdle && pageReferer.includes("/app")) {
      toast.custom(
        <DismissibleAlert>
          <span className="closebtn" onClick={() => toast.dismiss()}>
            &times;
          </span>
          You have been logged out automatically after 30 minutes of inactivity.
        </DismissibleAlert>,
        { duration: 9200000000000 }
      );
      return;
    }

    if (accessControlFailed && pageReferer.includes("/app")) {
      toast.custom(
        <DismissibleAlert>
          <span className="closebtn" onClick={() => toast.dismiss()}>
            &times;
          </span>
          You have been logged out immediately after trying to access a page
          with info you don&apos;t have the full permission(s) to see.
        </DismissibleAlert>,
        { duration: 9200000000000 }
      );
    }
  }, []);

  const handleFormSubmit = async (values: LoginInterface) => {
    const payload: LoginInterface = {
      email: values.email.toLowerCase(),
      password: values.password
    };

    const params = new URLSearchParams(window.location.search);
    /* @HINT: */
    const enableRudderstackAnalyticsTrackingOnProdOnly =
      config.API_ENDPOINT?.search(/(dev|staging)\./) === -1;

    userLogin(payload, {
      onSuccess: (response) => {
        if (response.v3LoginHash) {
          return window.location.replace(
            `${config.V3_APP_BASEURL}migrate-v2/verify?hash=${
              response.v3LoginHash
            }&email=${
              response?.user?.email ||
              response?.merchant?.email ||
              values.email.toLowerCase()
            }`
          );
        }
        toast.success("Welcome");
        storage.set(pwdKey, encrypt_key(values.password));
        const merchant: MerchantInterface = response.merchant;
        const member: ITeamMemberResponseObject = response.user;

        const [defaultBusinessInfo] = merchant.businesses;
        const allBusinessLiveStatus = merchant.businesses.reduce(
          (businessesLiveStatuses, business) => {
            return {
              ...businessesLiveStatuses,
              [business.id]: business.display_state !== "test"
            };
          },
          {} as { [key: string]: boolean }
        );

        window.sessionStorage.setItem(
          "allBusinessLiveStatus",
          JSON.stringify(allBusinessLiveStatus)
        );

        storage.set(userKey, encrypt_userobj(merchant));
        storage.set(defaultBusinessKey, encrypt_userobj(defaultBusinessInfo));
        storage.set(memberKey, encrypt_userobj(member));
        sessionStorage.setItem("lastTimeStamp", moment().format());

        /* @HINT: `config.PROD` is used to emphasize when this app is on staging or production */
        if (config.PROD) {
          Bugsnag.addMetadata("user_business_info", {
            trading_name: merchant.trading_name,
            branches: merchant.businesses
          });

          /* @HINT: Rudderstack SPA user action tracking */
          /* @NOTE: Rudderstack is the platform Duplo uses to gather and aggregate analytics 
              about real-time user actions/behavior on this app */

          rudderanalytics.track(
            "form_submit:logged_in",
            {
              page: document.title,
              page_url: window.location.href,
              page_search: window.location.search,
              page_history_state: {},
              form_id: "login_form",
              label: "logged_in",
              utm_source: params.get("utm_source") || "",
              utm_medium: params.get("utm_medium") || "",
              utm_content: params.get("utm_content") || "",
              utm_campaign: params.get("utm_campaign") || "",
              utm_term: params.get("utm_term") || ""
            },
            {
              integrations: {
                All: enableRudderstackAnalyticsTrackingOnProdOnly
              }
            },
            () => {
              rudderanalytics.identify(
                merchant.id,
                {
                  firstName: merchant.first_name,
                  lastName: merchant.last_name,
                  phone: merchant.phone_number,
                  email: merchant.email,
                  company: merchant.trading_name,
                  createdAt: merchant.update_at
                },
                {
                  page: {
                    path: window.location.pathname,
                    referrer: document.referrer,
                    search: window.location.search,
                    title: "Duplo - Login",
                    url: window.location.href
                  }
                },
                () => {
                  if (member && member.business) {
                    /* @HINT: Track user identity for Hotjar analytics */
                    identifyOnHotjar(member.id, {
                      account_merchnat_id: member.merchant_id,
                      account_user_first_name: member.first_name,
                      account_user_last_name: member.last_name,
                      account_user_email: member.email,
                      account_confirmed_email: member.email_confirm || false,
                      account_two_fa: member.two_fa || false,
                      account_user_phone_number: member.phone_number,
                      account_user_role: member.role?.name || "view_only",
                      account_business_activation_requested:
                        member.business?.activation_request || false,
                      account_business_type:
                        member.business?.business_type || "starter",
                      account_business_registration_type:
                        member.business?.registration_type || "incorporation",
                      account_business_name:
                        member.business?.trading_name ||
                        member.business?.legal_business_name ||
                        "unknown",
                      account_business_size:
                        member.business?.company_size || "",
                      account_business_activated: member.is_active || false
                    });
                  }

                  /* @HINT: Track user action event [LOGGEN-IN] for Hotjar analytics */
                  trackEventForHotjar("window_app_account_user_logged-in");
                  window.location.reload();
                }
              );
            }
          );
        } else {
          window.location.reload();
        }
      },
      onError: (err) => {
        if (!("message" in err)) {
          if (err.length > 0) {
            if (config.PROD) {
              rudderanalytics.track(
                "form_submit:login_error",
                {
                  page: document.title,
                  page_url: window.location.href,
                  page_search: window.location.search,
                  page_history_state: {},
                  form_id: "login_form",
                  label: "login_error",
                  utm_source: params.get("utm_source") || "",
                  utm_medium: params.get("utm_medium") || "",
                  utm_content: params.get("utm_content") || "",
                  utm_campaign: params.get("utm_campaign") || "",
                  utm_term: params.get("utm_term") || ""
                },
                {
                  integrations: {
                    All: enableRudderstackAnalyticsTrackingOnProdOnly
                  }
                }
              );
            }
            err.forEach((err) => toast.error(err as string));
            return;
          }
        } else {
          if (typeof err.message !== "string") {
            return;
          }

          toast.error(`${err.message}`);
        }
      }
    });
  };

  React.useEffect(() => {
    storeAuthHeader("Sign in to your account");
    //eslint-disable-next-line
  }, []);

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={loginSchema}
        onSubmit={async (values) => {
          await handleFormSubmit(values);
        }}
      >
        {({ handleSubmit }: FormikProps<LoginInterface>) => (
          <form autoComplete="off" onSubmit={handleSubmit}>
            <InputComponent
              name="email"
              title="Email Address"
              placeholder={isMobile ? "" : "Email address"}
              type="email"
            />
            <PasswordInputComponent
              name="password"
              title="Password"
              placeholder={isMobile ? "" : "Password"}
            />
            <ForgotPasswordText
              onClick={() => history.push(Routes.ForgotPassword)}
            >
              Forgot your password?
            </ForgotPasswordText>

            <AuthButtonContainer>
              <CustomButton
                type="submit"
                label="Sign in"
                isLoading={isLoading}
                height={50}
                disabled={isLoading}
                bgColor="#006355"
              />
            </AuthButtonContainer>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default Login;
